import './UserMonitors.scss';
import { memo, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import cx from 'classnames';
import _ from 'lodash';
import { AxiosError } from 'axios';

import {
  Button,
  Grid,
  GridItem,
  Icons,
  Kanban,
  notify,
  PendingContent,
  SearchInputField,
  Space,
  Spinner,
} from 'plume-ui';
import { useFetch, Maybe } from 'plume-web-utils';

import { KanbanGroupType } from 'plume-ui/dist/components/Kanban/types';

import MonitorCell from '../../components/MonitorCell/MonitorCell';
import { Routes } from '../../../../config';
import { AdjustableComponent } from '../../../../utils/hooks/useClasses';
import { DependencyContainer } from '../../../../DependencyContainer';
import { AvailableScreens } from '../../../trackingAnalytics/types';
import { MixPanelEvents } from '../../../../mixPanelEvents';
import { activePartnerAtom } from '../../../../state';

import { Permission } from '../../../../userPermissionConfig';
import { Monitor, MonitorCellProps } from '../../types';
import { useFeaturePermission } from '../../../../hooks/useFeaturePermission';

import useWithConfirmation from '../../../../utils/hooks/useWithConfirmation';
import { useTrackEvent } from '../../../trackingAnalytics/hooks/useTrackEvent';
import { useRedirectToRoute } from '../../../../utils/hooks/useRedirectToRoute';
import { toOrderedMonitors } from '../../utils/monitorUtils';
import { UseFetchCacheKeys } from '../../../../cacheConfig';

const { monitorsService } = new DependencyContainer();

type MonitorProps = {
  test?: number | 200;
};

type MonitorStylesType = {};

const UserMonitors: AdjustableComponent<
  MonitorProps,
  MonitorStylesType
> = () => {
  const { t } = useTranslation();
  const { hasPermission } = useFeaturePermission();
  const withConfirmation = useWithConfirmation();

  const [reorderMode, setReorderMode] = useState(false);
  const [reorderGroups, setReorderGroups] = useState<
    KanbanGroupType<MonitorCellProps & { reorder?: number }>[]
  >([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [groupsTouched, setGroupsTouched] = useState(false);

  const trackEvent = useTrackEvent();

  const activePartner = useRecoilValue(activePartnerAtom);
  const redirectToRoute = useRedirectToRoute();

  const [searchQuery, setSearchQuery] = useState<string>('');

  const {
    isLoading: loadingMonitors,
    data: monitors,
    error,
    refetch: runFetchMonitorList,
    isComplete: monitorsFetchComplete,
  } = useFetch(
    async () => {
      if (!activePartner?.id) return;
      let listItems = await monitorsService.getMonitorListItems(
        activePartner.id,
      );
      return toOrderedMonitors(listItems);
    },
    {
      dependencies: [activePartner],
      skip: activePartner === undefined,
      cacheKey: UseFetchCacheKeys.UserMonitorList,
    },
  );

  const filtered = useMemo(() => {
    if (!searchQuery) {
      return monitors;
    }
    return monitors?.filter((monitor) =>
      monitor.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [searchQuery, monitors]);

  useEffect(() => {
    trackEvent({
      eventName: MixPanelEvents.SCREEN,
      additionalContent: {
        SCREEN: AvailableScreens.Monitors,
      },
    });
  }, []);

  const monitorData: Maybe<MonitorCellProps[]> = useMemo(
    () =>
      filtered?.map((monitor: Monitor, index) => {
        return {
          name: monitor.name,
          id: monitor.id || '',
          supportOutreach: monitor.outreach?.supportEmail,
          subscriberOutreach: monitor.outreach?.subscriberPush,
          supportWebhook: monitor.outreach?.supportWebhook,
          index: index,
          frequencyInHours: monitor.frequencyInHours || undefined,
        };
      }),
    [filtered],
  );

  const menuCallbackHandlers = {
    delete: (id?: string) => {
      if (!id) return;
      // lookup name from id
      const monitor = monitorData?.find(
        (monitorSearch: MonitorCellProps) => monitorSearch.id === id,
      );

      withConfirmation({
        title: t('monitors.deleteConfirmationTitle'),
        body: t('monitors.deleteConfirmationMessage', {
          monitorName: monitor?.name,
        }),
        confirmButtonLabel: t('delete'),
        onConfirm: async () => {
          if (!activePartner?.id) return;
          await monitorsService.deleteMonitor(activePartner.id, id);
          trackEvent({
            eventName: MixPanelEvents.MONITOR_DELETE,
            additionalContent: {
              MONITOR_ID: id,
            },
          });
          runFetchMonitorList();
        },
      });
    },

    redirectToOutreachConfig: (id?: string) => {
      if (!id) return;
      if (!activePartner?.id) {
        return;
      }
      redirectToRoute(Routes.OutreachEdit, {
        partnerId: activePartner.id,
        monitorId: id,
        anchor: '',
      });
    },

    edit: (id?: string) => {
      if (!id) return;
      if (!activePartner?.id) {
        return;
      }
      trackEvent({
        eventName: MixPanelEvents.MONITOR_EDIT,
        additionalContent: {
          MONITOR_ID: id,
        },
      });
      redirectToRoute(Routes.MonitorEdit, {
        partnerId: activePartner?.id,
        monitorId: id,
      });
    },
  };

  const navigateToAddMonitor = () => {
    trackEvent({
      eventName: MixPanelEvents.MONITOR_ADD,
    });
    redirectToRoute(Routes.MonitorAdd, { partnerId: activePartner?.id });
  };

  const mapMonitorsToReorderGroups = (monitors: MonitorCellProps[]) => {
    return monitors.map((monitor) => ({
      name: _.uniqueId(),
      id: _.uniqueId(),
      items: [monitor],
    }));
  };

  const onToggleReorderMode = () => {
    setReorderMode((prev) => {
      if (prev) {
        if (!searchQuery && monitorData)
          setReorderGroups(mapMonitorsToReorderGroups(monitorData));
        setSearchQuery('');
      }
      setGroupsTouched(false);
      return !prev;
    });
  };

  const handleGroupChange = (newGroups: typeof reorderGroups) => {
    // find group where we transfered the item to
    const twoItemGroupIndex = newGroups.findIndex((g) => g.items.length === 2);
    if (twoItemGroupIndex === -1) {
      setReorderGroups(newGroups);
      return;
    }

    // reorder logic to minimize PATCH calls
    // won't need this if API decides to give us one endpoint for reordering
    newGroups[twoItemGroupIndex].items[0].reorder = twoItemGroupIndex;

    const orderedGroups = [...newGroups];

    let tempItem: typeof reorderGroups[number]['items'][number] | undefined;

    // shifting items left and right logic
    for (let i = 0; i < orderedGroups.length; i++) {
      if (i < twoItemGroupIndex) {
        if (orderedGroups[i].items.length === 0) {
          const nextGroupLength = orderedGroups[i + 1].items.length;
          const item = orderedGroups[i + 1].items[nextGroupLength - 1];
          item.reorder = i;
          orderedGroups[i].items = [item];
          orderedGroups[i + 1].items.splice(nextGroupLength - 1, 1);
        }
      } else {
        if (orderedGroups[i].items.length === 2) {
          tempItem = orderedGroups[i + 1].items[0];
          const oldItem = orderedGroups[i].items[1];
          oldItem.reorder = i + 1;
          orderedGroups[i + 1].items = [oldItem];
          orderedGroups[i].items.splice(1, 1);
          i++;
        } else if (tempItem !== undefined) {
          const tmp = tempItem;
          tmp.reorder = i;
          tempItem = orderedGroups[i].items[0];
          orderedGroups[i].items = [tmp];
        }
      }
    }
    setGroupsTouched(true);
    setReorderGroups(orderedGroups);
  };

  const onReorderSave = async () => {
    if (!activePartner?.id) return;
    try {
      const reorderedMonitors = reorderGroups
        .map((group) => group.items[0])
        .filter((item) => item.reorder !== undefined);
      setSavingOrder(true);
      const patchRequests = reorderedMonitors.map((monitor) =>
        monitorsService.patchMonitor(activePartner.id as string, monitor.id, {
          name: monitor.name,
          monitorPositionInfo: { order: monitor.reorder },
        }),
      );
      await Promise.all(patchRequests);
      trackEvent({
        eventName: MixPanelEvents.REORDER_MONITORS,
        additionalContent: {
          TOTAL_MONITORS: monitorData?.length || 0,
          MONITORS_WITH_ORDER: reorderedMonitors.length,
          PERCENTAGE_ORDERED:
            +(reorderedMonitors.length / (monitorData?.length || 1)) * 100,
        },
      });
      runFetchMonitorList();
      setReorderMode(false);
      setSavingOrder(false);
      setGroupsTouched(false);
      notify({
        title: t('monitors.reorder.success'),
        type: 'success',
        body: t('monitors.reorder.successMessage'),
      });
    } catch (e) {
      console.error('Error saving reorder', e);
      notify({
        title: t('monitors.reorder.error'),
        type: 'error',
        body: (e as AxiosError).message,
      });
      setSavingOrder(false);
      setGroupsTouched(false);
    }
  };

  useEffect(() => {
    if (monitorData && monitorData.length === monitors?.length)
      setReorderGroups(mapMonitorsToReorderGroups(monitorData));
  }, [monitorData]);

  return (
    <span className="UserMonitors">
      <Grid>
        <GridItem colSpan="12" tabletColSpan="12">
          <div className="UserMonitors__header">
            <div className="UserMonitors__headerLeft">
              <SearchInputField
                id={'monitorSearch'}
                rounded
                disabled={reorderMode}
                placeholder={t('monitors.searchMonitor.title')}
                onChange={(event) => {
                  setSearchQuery((event.target as HTMLInputElement).value);
                  // fake resize to recalculate tooltip positions :/
                  // tooltips might benefit from an observer
                  window.dispatchEvent(new Event('resize'));
                }}
              />
            </div>
            <div className="UserMonitors__headerRight">
              <div className="UserMonitors__reorderHeader">
                {!searchQuery && (
                  <>
                    <Button
                      icon={
                        reorderMode ? (
                          <Icons.CrossIcon />
                        ) : (
                          <Icons.ControlsIcon />
                        )
                      }
                      onClick={onToggleReorderMode}
                      styleVariant="tertiary-grey"
                      disabled={savingOrder}
                      animateLayout
                    >
                      {reorderMode
                        ? t('monitors.reorder.cancel')
                        : t('monitors.reorder.action')}
                    </Button>
                    {reorderMode && (
                      <Button
                        onClick={onReorderSave}
                        icon={<Icons.CheckIcon />}
                        styleVariant="tertiary"
                        loading={savingOrder}
                        disabled={!groupsTouched}
                        animateLayout
                      >
                        {t('monitors.reorder.save')}
                      </Button>
                    )}
                  </>
                )}
              </div>
              <Button
                styleVariant="superprimary"
                disabled={!hasPermission(Permission.AddMonitor) || reorderMode}
                onClick={() => {
                  navigateToAddMonitor();
                }}
                classes={(current) => ({
                  ...current,
                  root: `${current.root} Monitors__create-monitor-btn`,
                })}
              >
                {t('monitors.add')}
              </Button>
            </div>
          </div>
        </GridItem>
      </Grid>
      <Space size="s" />
      <PendingContent
        loading={loadingMonitors}
        loader={Spinner}
        isError={Boolean(error)}
        transparent
      >
        {!reorderMode && (
          <Grid>
            {monitorsFetchComplete && monitorData ? (
              monitorData.length > 0 ? (
                monitorData.map((monitor) => (
                  <GridItem
                    key={monitor.id || monitor.name}
                    colSpan="4"
                    tabletColSpan="3"
                  >
                    <div className="UserMonitors__card">
                      <MonitorCell
                        name={monitor.name}
                        id={monitor.id}
                        supportOutreach={monitor.supportOutreach}
                        subscriberOutreach={monitor.subscriberOutreach}
                        supportWebhook={monitor.supportWebhook}
                        menuHandler={menuCallbackHandlers}
                        frequencyInHours={monitor.frequencyInHours}
                      />
                    </div>
                  </GridItem>
                ))
              ) : (
                <GridItem colSpan="12" tabletColSpan="12">
                  <div className="UserMonitors__header__no-results">
                    {t('monitors.searchMonitor.noResults')}
                  </div>
                </GridItem>
              )
            ) : null}
          </Grid>
        )}

        {reorderMode && (
          <div>
            <Kanban
              groups={reorderGroups}
              className="UserMonitors__reorderContainer Grid"
              onChange={handleGroupChange}
              groupRender={({ content, isActive }) => (
                <div
                  className={cx('ReorderGroup', {
                    'ReorderGroup--active': isActive,
                  })}
                >
                  {content}
                </div>
              )}
              itemKey={(item) => item.id}
              itemRender={({ item }) => (
                <div className="UserMonitors__card Monitors__card--reorder MonitorCell">
                  <div className="UserMonitors__cardHeader">{item.name}</div>
                  <div className="UserMonitors__cardDragToReorder">
                    {t('monitors.reorder.dragToReorder')}
                  </div>
                </div>
              )}
            />
          </div>
        )}
      </PendingContent>
    </span>
  );
};

export default memo(UserMonitors);
